import { DataMapper } from '../../api/transport';
import { action, computed, observable, reaction } from 'mobx';
import { IOptionItem } from '@estee/elc-dropdown';
import { ADDRESS_TYPE, FIELD_TYPE, DEFAULT_ADDRESS_SUGGESTION_FIELDS, INPUT_TYPE, TYPEAHEAD_SEARCH_TRIGGER_LENGTH } from '../../constants/Constants';
import { getFieldDependenciesBySchema } from '../../utils/AddressValidation';
import AddressRepository from '../../repositories/AddressRepository';
import AddressFormStore from '../../domain/entities/AddressFormStore';
import Config, { IDropdownOption, IFieldDependencies, IState } from '../../domain/entities/Config';
import TransactionRepository from '../../repositories/TransactionRepository';
import ConfigRepository from '../../repositories/ConfigRepository';
import NotificationsCollection from '../../domain/entities/Notification/NotificationsCollection';
import Address, { IAddressAttributes } from '../../domain/entities/Address';
import { IAddressSchemaProperties, IAddressSchema } from '../../api/JsonSchemaApi';
import AddressTreeNodesRepository from '../../repositories/AddressTreeNodesRepository';
import { IAddressTreeNode } from '../../repositories/AddressTreeNodesRepository/AddressTreeNodesRepository';
import { NotificationsService } from '../../application/services/NotificationsService';
import { FEATURE } from '../../constants/Features';
import { debounce } from '../../utils/Debounce';
import DEFAULT_ADDITIONAL_PROPERTIES_SCHEMA from '../../constants/DefaultAdditionalPropertiesSchema';
import { NOTIFICATION_GROUP } from '../../constants/Notifications';
import { ADDRESS } from '../../constants/Errors';

export interface IAddressViewControllerConfig {
  transactionRepository: TransactionRepository;
  configRepository: ConfigRepository;
  notificationsCollection: NotificationsCollection;
  notificationsService: NotificationsService;
  dataMapper: DataMapper;
  addressTreeNodesRepository: AddressTreeNodesRepository;
  addressRepository: AddressRepository;
}

export default class AddressViewController {
  @observable protected addressRepository: AddressRepository;

  protected transactionRepository: TransactionRepository;

  protected configRepository: ConfigRepository;

  @observable protected addressTreeNodesRepository: AddressTreeNodesRepository;

  protected notificationsCollection: NotificationsCollection;

  protected notificationsService: NotificationsService;

  @observable fieldDependencies: IFieldDependencies = {};

  @observable multipleCityState: IOptionItem[] = null;

  @observable countryId: number;

  @observable _statesList: IState[] = [];

  @observable cities: IDropdownOption[] = [];

  @observable colonies: string[] = [];

  @observable towns: string[] = [];

  @observable districts: string[] = [];

  @observable streets: IDropdownOption[] = [];

  @observable wards: IDropdownOption[] = [];

  @observable addressDropdownOptions: Map<string, IDropdownOption[]>;

  @observable private addressSuggestions: Address[];

  private dataMapper: DataMapper;

  @observable protected isLoading = false;

  @observable protected hasSuggestions = true;

  private disposers: (() => void)[] = [];

  @observable shouldShowDeleteModal = false;

  constructor(config: IAddressViewControllerConfig) {
    this.transactionRepository = config.transactionRepository;
    this.configRepository = config.configRepository;
    this.addressTreeNodesRepository = config.addressTreeNodesRepository;
    this.notificationsCollection = config.notificationsCollection;
    this.notificationsService = config.notificationsService;
    this.dataMapper = config.dataMapper;
    this.addressRepository = config.addressRepository;

    reaction(() => this.addressRepository.activeSchema, this.handleSchemaUpdates);
    reaction(() => this.statesList, options => this.autoSetValueOnOneOption(options, FIELD_TYPE.STATE));
    reaction(() => this.cities, options => this.autoSetValueOnOneOption(options, FIELD_TYPE.CITY));
    reaction(() => this.streets, options => this.autoSetValueOnOneOption(options, FIELD_TYPE.FIRST_LINE_ADDRESS));
    reaction(() => this.wards, options => this.autoSetValueOnOneOption(options, FIELD_TYPE.SECOND_LINE_ADDRESS));
    reaction(() => this.features, features => this.formStore.setFeatures(features));
    // This is only used to support legacy schema
    reaction(() => this.statesList?.length, statesCount => this.updateSchema({ hasStates: !!statesCount }));
    reaction(() => this.formStore?.values?.[FIELD_TYPE.COUNTRY_ID], countryId => this.updateAddressDataBasedOnCountry(countryId));

    this.debouncedFirstLineAddressSearch = debounce(this.searchAddress, 400);
  }

  public dispose(): void {
    this.disposers.forEach(disposer => disposer());
  }

  @action
  async loaded(): Promise<void> {
    await this.configRepository.getConfig();
    this.setCountryId(this.config.countryId);

    await this.transactionRepository.getTransaction();
    const { transaction } = this.transactionRepository;

    const { addressType } = this.addressRepository;
    let addressCountryId;

    switch (addressType) {
      case ADDRESS_TYPE.SHIPPING:
      case ADDRESS_TYPE.PACKSTATION:
        addressCountryId = transaction?.shippingAddress?.attributes?.[FIELD_TYPE.COUNTRY_ID];
        break;
      case ADDRESS_TYPE.BILLING:
        addressCountryId = transaction?.billingAddress?.attributes?.[FIELD_TYPE.COUNTRY_ID];
        break;
    }

    this.formStore.setFieldValue(FIELD_TYPE.COUNTRY_ID, addressCountryId || this.countryId);
    // although this should be handle by reaction to country id change, 
    // there is an issue with component remounting 2 or 3 times, due to how delivery tabs are being enabled or not
    // so it is safer to enforce this on every load, otherwise address schema might not be set 
    await this.updateAddressDataBasedOnCountry(addressCountryId || this.countryId);

    this.setAddressDropdownOptions(new Map<string, IDropdownOption[]>());

    if (transaction?.user?.isProCustomer) {
      this.formStore.setFieldValue(FIELD_TYPE.FIRST_NAME, transaction?.user?.firstName);
      this.formStore.setFieldValue(FIELD_TYPE.LAST_NAME, transaction?.user?.lastName);
    }
  }

  // some data might require some address form adjustments based on schema
  public adjustFormValuesBasedOnSchema(): void {
    this.formStore.adjustFormValuesBasedOnSchema();
  }

  private debouncedFirstLineAddressSearch: (field: FIELD_TYPE, keywords: string) => Promise<Address[] >;

  @computed
  get isShippingCountrySelect(): boolean {
    const shippingAddressInputSchema = this.formStore.addressSchema;
    const countryInputType = shippingAddressInputSchema?.properties?.country_id?.additionalProperties?.input_type;
    return countryInputType === INPUT_TYPE.SELECT;
  }

  @action
  setCountryId = (countryId: number): void => {
    this.countryId = countryId;
  };

  @action
  setAddressDropdownOptions = (options: Map<string, IDropdownOption[]>): void => {
    this.addressDropdownOptions = options;
  };

  protected get config(): Config {
    const { config } = this.configRepository;
    return config;
  }

  @computed
  private get features(): FEATURE[] {
    let features: FEATURE[] = [];

    if (this.transactionRepository.transaction?.user?.isProCustomer) {
      features = [...features, FEATURE.PRO_CUSTOMER];
    }

    return features;
  }

  @computed
  protected get formStore(): AddressFormStore {
    return this.addressRepository.formStore;
  }

  @computed
  protected get statesList(): IState[] {
    return this.addressStaticJson
      ? this.getOptionsFromTreeRows(this.addressStaticJson)
      : this.formStore?.schemaDropdownOptions?.[FIELD_TYPE.STATE] || this._statesList;
  }

  @computed
  protected get addressSchema(): IAddressSchemaProperties {
    return this.formStore?.addressSchema?.properties;
  }

  @computed
  protected get validationSchema(): IAddressSchema {
    return this.formStore?.addressSchema;
  }

  @computed
  get shouldEnableNewSchema(): boolean {
    return this.addressRepository.shouldEnableNewSchema;
  }

  updateSchema(options: {
    hasStates: boolean;
  }): void {
    this.formStore.setAddressSchema(this.validationSchema, {
      hasStates: options.hasStates,
    });
  }

  @computed
  get isSameCountryAsConfig(): boolean {
    return this.formStore.values[FIELD_TYPE.COUNTRY_ID] === this.config.countryId;
  }

  @computed
  get sortedAddresses(): Address[] {
    return this.addressRepository.addresses.sort((a1: Address, a2: Address) => a1.compare(a2));
  }

  @computed
  get selectedCountryId(): number {
    return this.formStore.values[FIELD_TYPE.COUNTRY_ID] || this.countryId;
  }

  @action
  public onSelectFromTypeahead = async (
    field: keyof IAddressAttributes,
    value: string, // Address ID
  ): Promise<void> => {
    this.streets = [];
    const autoFillFields = this.formStore.getFieldAutoFillFields(field);
    const address = this.addressSuggestions.find(addr => addr.clientId === value);
    autoFillFields.forEach(fieldName => {
      this.formStore.setFieldValue(fieldName, address.attributes[fieldName]);
    });
  };

  @action
  public onSelectFromAddressSearch = async (
    field: keyof IAddressAttributes,
    keywords: string,
    value: string,
  ): Promise<void> => {
    const address = await this.addressRepository
      .getAddressFieldsFromSelectedSuggestionValueLegacy(keywords, value, this.formStore.values[FIELD_TYPE.COUNTRY_ID]);
    const autoFillFields = this.formStore.getFieldAutoFillFields(field);
    autoFillFields.forEach(fieldName => {
      this.formStore.setFieldValue(fieldName, address[fieldName]);
    });
  };

  @action
  public onSelectFromZipcode = async (field: keyof IAddressAttributes, value: string): Promise<void> => {
    const schemaAutoFillFields = this.formStore.getFieldAutoFillFields(field);
    const selectedAddress = (this.addressSuggestions || []).find(addr => addr.clientId === value);
    if (selectedAddress) {
      schemaAutoFillFields.forEach(schemaField => {
        this.formStore.setFieldValue(schemaField, selectedAddress?.attributes[schemaField] || '');
      });
    }
    this.addressSuggestions = null;
    this.multipleCityState = null;
  };

  public searchAddressLegacy = (
    keywords: string,
  ): Promise<{ label: string, value: string }[]> => {
    return this.addressRepository.getSuggestionFromAddressSearchLegacy(keywords, this.formStore.values[FIELD_TYPE.COUNTRY_ID]);
  };

  @action
  public onChangeFirstLineAddress =  async (value: string): Promise<void> => {
    const inputIsTypeahead = this.addressSchema?.address1?.additionalProperties?.input_type === INPUT_TYPE.TYPEAHEAD;
    if (
      inputIsTypeahead
      && this.isSameCountryAsConfig
      && this.config.supportsTypeaheadInput
      && value.length >= TYPEAHEAD_SEARCH_TRIGGER_LENGTH
    ) {
      this.addressSuggestions = await this.debouncedFirstLineAddressSearch(FIELD_TYPE.FIRST_LINE_ADDRESS, value);
      this.streets = this.getSuggestionAddressList(FIELD_TYPE.FIRST_LINE_ADDRESS, this.addressSuggestions);
    }
  };

  @computed
  private get hasTypeaheadInput(): boolean {
    const hasTypeaheadInputOnSchema = Object.values(this.formStore.addressSchema.properties)
      .some(field => field.additionalProperties?.input_type === INPUT_TYPE.TYPEAHEAD);
    return hasTypeaheadInputOnSchema && this.config.supportsTypeaheadInput;
  }

  @action
  public onZipChange = async (field: keyof IAddressAttributes, value: string): Promise<void> => {
    if (
      this.config.useCityStateLookup
      && this.isSameCountryAsConfig
      // When there is a typeahead input we don't want the zip to also have this functionality
      && !this.hasTypeaheadInput
    ) {
      const fieldSchema = this.validationSchema.properties[field];
      const pattern = new RegExp(fieldSchema.pattern);
      this.hasSuggestions = true;
      if (pattern.test(value)) {
        const schemaAutoFillFields = this.formStore.getFieldAutoFillFields(field);
        this.addressSuggestions = await this.addressRepository.getAddressFromZip({
          fields: schemaAutoFillFields,
          zipcode: value,
          zipcodeApiPath: fieldSchema.additionalProperties?.r,
        });
        if (this.addressSuggestions.length === 1) {
          this.onSelectFromZipcode(field, this.addressSuggestions[0].clientId);
          const { colony, municipality } = this.addressSchema;
          if (colony && colony.additionalProperties
            && municipality && municipality.additionalProperties) {
            const response = await this.addressRepository.getMunicipalityandColoniesByZip(value);
            this.colonies = response.colonies;
            this.formStore.setFieldValue(FIELD_TYPE.MUNICIPALITY, response.municipality);
          }
        } else if (this.addressSuggestions.length > 1) {
          this.multipleCityState = this.getSuggestionAddressList(field, this.addressSuggestions);
        } else if (fieldSchema.additionalProperties?.validate_existence) {
          this.hasSuggestions = false;
          const autoFillFields = this.formStore.getFieldAutoFillFields(field);
          this.addressRepository.clearAutoFillFields(autoFillFields);
        }
      }
    }
  };

  private searchAddress = (
    field: keyof IAddressAttributes,
    keywords: string,
  ): Promise<Address[]> => {
    const suggestionFields = (this.addressSchema
      && this.addressSchema[field]
        ?.additionalProperties
        ?.address_suggestion_fields)
          || DEFAULT_ADDRESS_SUGGESTION_FIELDS;
    return this.addressRepository.getSuggestionFromAddressSearch(keywords, suggestionFields);
  };

  private getSuggestionAddressList = (field: keyof IAddressAttributes, addresses: Address[]): IOptionItem[] => {
    const addressSuggestionFields = this.formStore.getFieldSuggestionFields(field);
    return addresses.map(address => {
      const { clientId, attributes } = address;
      let label = '';
      addressSuggestionFields.forEach(suggestField => {
        if (attributes[suggestField]) {
          label += label ? `, ${attributes[suggestField]}` : attributes[suggestField];
        }
      });
      return {
        label,
        value: clientId,
      };
    });
  };

  @action
  public onChangeCity = async (city: string): Promise<void> => {
    const { address1, address2, address3 } = this.addressSchema;
    if (
      this.addressStaticJson
      && address1
      && address1.additionalProperties
      && address1.additionalProperties.staticLookupName
    ) {
      const { state } = this.formStore.values;
      const cities = this.addressStaticJson.find(record => record.value === state)?.subtree;
      const selectedCity = cities?.find(record => record.value === city);
      this.streets = selectedCity ? this.getOptionsFromTreeRows(selectedCity.subtree) : [];
    }

    if (address3 && address3.additionalProperties && address3.additionalProperties.r) {
      const towns = await this.addressRepository.getTownsByCity(city);
      this.towns = towns;
    }

    if (this.addressStaticJson && address2?.additionalProperties?.staticLookupName) {
      const { state } = this.formStore.values;
      const cities = this.addressStaticJson.find(record => record.value === state)?.subtree;
      const selectedCity = cities?.find(record => record.value === city);
      this.wards = selectedCity ? this.getOptionsFromTreeRows(selectedCity.subtree) : [];
    }

  };

  @action
  public onChangeState = (state: string): void => {
    if (!this.addressSchema) {
      return;
    }

    const { city, address2 } = this.addressSchema;
    if (this.addressStaticJson
      && city
      && city.additionalProperties
      && city.additionalProperties.staticLookupName
    ) {
      const selectedState = this.addressStaticJson.find(record => record.value === state);
      this.cities = selectedState ? this.getOptionsFromTreeRows(selectedState.subtree) : [];
    }

    if (this.addressStaticJson && address2?.additionalProperties?.staticLookupName) {
      this.wards = [];
    }
  };

  @action
  public onChangeThirdLineAddress = async (address: string): Promise<void> => {
    const districts = await this.addressRepository.getDistrictsByCityTown({
      city: this.formStore.values.city as string,
      town: address,
    });

    this.districts = districts;
  };

  @action
  public onChangeFifthLineAddress = async (address: string): Promise<void> => {
    const response = await this.addressRepository.getStreetsAndPostalCodeByCityTownDistrict({
      city: this.formStore.values.city as string,
      district: address,
      town: this.formStore.values[FIELD_TYPE.THIRD_LINE_ADDRESS] as string,
    });

    this.streets = this.getOptionsFromStrings(response.streets);
    this.formStore.setFieldValue(FIELD_TYPE.ZIPCODE, response.postal_codes[0]);
  };

  @action
  public onCountryChange = async (countryId: number, countryLabel: string): Promise<void> => {
    this.setIsLoading(true);
    // If shippingCalculator is enabled, do not reload the shipping methods.
    // That will remove/replace the requested ones on postal code change
    if (this.addressRepository.addressType === ADDRESS_TYPE.SHIPPING
      && !this.config?.useShippingCalculator) {
      await this.transactionRepository.loadShippingMethods(countryId);
    }
    this.setIsLoading(false);

    // legacy schema will adapt zipcode and phone based on formstore's countryId, hence we need to have 
    // the value already set on form store, prior to setting the address schema
    this.formStore.setFieldValue(FIELD_TYPE.COUNTRY_ID, countryId);
    this.formStore.setFieldValue(FIELD_TYPE.COUNTRY, countryLabel);
  };

  @action
  public updateAddressDataBasedOnCountry = async (countryId: number): Promise<void> => {
    if (!countryId) {
      return;
    }

    let schema: IAddressSchema = this.validationSchema;
    if (this.shouldEnableNewSchema) { 
      this.setIsLoading(true);
      schema = await this.addressRepository.loadAddressSchema(countryId);
      if (schema) {
        this.setFieldDependenciesBySchema(schema.properties);
      }
    }

    if (schema) {
      this.formStore.setAddressSchema(schema, {
        hasStates: !!this.statesList?.length,
      });

      await this.loadAddressOptions({ addressSchema: schema.properties, countryId });
    }
    this.setIsLoading(false);

    // multi language： countryId is not changed and there is no refresh on state list
    // manually check 'auto set value on state if one option' once country changes
    this.autoSetValueOnOneOption(this.statesList, FIELD_TYPE.STATE);
  };

  @action
  private setIsLoading = (isLoading: boolean): void => {
    this.isLoading = isLoading;
  };

  public onChangeAddressField = async (addressField: keyof IAddressAttributes):
  Promise<void> => {
    const fieldDeps = this.fieldDependencies[addressField];
    const filteredFieldDeps = fieldDeps.filter(({ targetUrl }) => !!targetUrl);

    if (filteredFieldDeps) {
      await Promise.all(filteredFieldDeps.map(
        async ({ fieldName, responseKey, targetUrl }) => {
          if (!targetUrl) {
            return;
          }

          const formattedUrl = this.buildUrlFromDependencyFields(targetUrl);
          const options = await this.addressRepository.getGenericAddressOptions(
            formattedUrl,
            responseKey,
          );

          // unset current value of dependent field
          this.formStore.setFieldValue(fieldName, '');

          // populate options of dependent field
          if (options.length === 1) {
            this.formStore.setFieldValue(fieldName, options[0].value);
          }

          this.addressDropdownOptions.set(fieldName, options);
        },
      ));
    }
  };

  private buildUrlFromDependencyFields = (templateUrl: string): string => {
    const placeholders = templateUrl.match(/:[a-z0-9]*/g);

    if (!placeholders) {
      return templateUrl;
    }

    return placeholders.reduce((acc: string, curr: string) => {
      const formField = curr.substring(1) as keyof IAddressAttributes;
      const replacedUrl = acc.replace(curr, `${this.formStore.values[formField]}`);

      return replacedUrl;
    }, templateUrl);
  };

  @action
  public onCancelClearSelectOptions = (): void => {
    this.towns = [];
    this.districts = [];
    this.streets = [];
    this.colonies = [];
    this.hasSuggestions = true;
  };

  @action
  public onCloseCityStateDropdown = (): void => {
    this.addressSuggestions = null;
    this.multipleCityState = null;
  };

  protected setDefaultValues = (): void => {
    if (this.addressSchema) {
      const mappedSchema: IAddressSchemaProperties = this.dataMapper.mapResponse('toAddressSchema', this.addressSchema);

      (Object.keys(mappedSchema) as Array<keyof typeof mappedSchema>).forEach(propertyName => {
        if (
          mappedSchema[propertyName]
          && mappedSchema[propertyName].default
          && !this.formStore.values[propertyName]
        ) {
          this.formStore.setFieldValue(propertyName, mappedSchema[propertyName].default);
        }
      });
    }
  };

  @action
  protected handleSchemaUpdates = async (): Promise<void> => {
    this.setFieldDependenciesBySchema(this.addressSchema);
    await this.addressRepository.loadInputsTranslations();
  };

  @action
  protected setFieldDependenciesBySchema(schema: IAddressSchemaProperties): void {
    this.fieldDependencies = getFieldDependenciesBySchema(schema);
  }

  @computed
  public get addressStaticJson(): IAddressTreeNode[] {
    return this.addressTreeNodesRepository.addressTrees[this.addressRepository.addressListSource];
  }

  private getOptionsFromTreeRows = (options: IAddressTreeNode[]): IDropdownOption[] => {
    if (options && options.length) {
      return options.map(option => {
        const { label, value } = option;
        return {
          label: label || value,
          value,
        };
      });
    }
    return [];
  };

  private getOptionsFromStrings = (options: string[]): IDropdownOption[] => {
    if (options && options.length) {
      return options.map(option => ({
        label: option,
        value: option,
      }));
    }
    return [];
  };

  @action
  public async loadAddressOptions(data: {
    addressSchema?: IAddressSchemaProperties;
    countryId: number;
  }): Promise<void> {
    const { countryId, addressSchema } = data;
    const addressFieldSchema = addressSchema || this.addressSchema;
    if (!addressFieldSchema) return;

    const { setAddressListSource } = this.addressRepository;
    const stateSchema = addressFieldSchema.state || this.formStore.stateSchemaPropertyField || DEFAULT_ADDITIONAL_PROPERTIES_SCHEMA.state;
    const { city: citySchema, address1, address2 } = addressFieldSchema;
    const {
      getStates, cities, locale, staticDataLookupUrls,
    } = this.config;
    const isStateFromStatic = stateSchema
      && stateSchema.additionalProperties
      && stateSchema.additionalProperties.staticLookupName;
    if (isStateFromStatic) {
      const { getAddressTreeName, getAddressTree } = this.addressTreeNodesRepository;

      const { staticLookupName } = stateSchema.additionalProperties;
      const addressTreeName = getAddressTreeName(staticLookupName, locale, countryId);
      setAddressListSource(addressTreeName);

      // state list from static json
      const staticJson = await getAddressTree({
        countryId,
        locale,
        staticDataLookupUrl: staticDataLookupUrls[staticLookupName],
        staticLookupName,
      });
      const { state, city } = this.formStore.values;
      const isCityFromStatic = citySchema
        && citySchema.additionalProperties
        && citySchema.additionalProperties.staticLookupName;

      if (isCityFromStatic) {
        // city list from static json
        this.cities = [];
        if (state) {
          const selectedState = staticJson.find(record => record.value === state);
          this.cities = selectedState && selectedState.subtree
            ? this.getOptionsFromTreeRows(selectedState.subtree)
            : [];

          const isAddress1FromStatic = address1
            && address1.additionalProperties
            && address1.additionalProperties.staticLookupName;
          if (isAddress1FromStatic) {
            this.streets = [];
            if (city && selectedState) {
              const selectedCity = selectedState.subtree.find(record => record.value === city);
              this.streets = selectedCity && selectedCity.subtree
                ? this.getOptionsFromTreeRows(selectedCity.subtree)
                : [];
            }
          }

          const isAddress2FromStatic = address2?.additionalProperties?.staticLookupName;
          if (isAddress2FromStatic) {
            this.wards = [];
            if (city && selectedState) {
              const selectedCity = selectedState.subtree.find(record => record.value === city);
              this.streets = selectedCity?.subtree
                ? this.getOptionsFromTreeRows(selectedCity.subtree)
                : [];
            }
          }

        }
      } else {
        // city list from data-config
        this.cities = this.getOptionsFromStrings(cities);
      }
    } else {
      setAddressListSource('config');
      if (!this.shouldEnableNewSchema) {
        this._statesList = getStates(countryId);
      } else {
        this._statesList = addressFieldSchema?.state?.additionalProperties?.options || [];
      }
      this.cities = this.getOptionsFromStrings(cities);
    }
  }

  @action
  public getOptionsForEditForm = async (): Promise<void> => {
    const { 
      [FIELD_TYPE.CITY]: city,
      [FIELD_TYPE.THIRD_LINE_ADDRESS]: thirdLineAddress,
      [FIELD_TYPE.FIFTH_LINE_ADDRESS]: fifthLineAddress,
    } = this.formStore.values;
    if (city) {
      const towns = await this.addressRepository.getTownsByCity(city);
      this.setTowns(towns);
    }
    if (city && thirdLineAddress) {
      const districts = await this.addressRepository.getDistrictsByCityTown({
        city,
        town: thirdLineAddress as string,
      });
      this.setDistricts(districts);
    }
    if (city && thirdLineAddress && fifthLineAddress) {
      const response = await this.addressRepository.getStreetsAndPostalCodeByCityTownDistrict({
        city: city as string,
        district: fifthLineAddress as string,
        town: thirdLineAddress as string,
      });
      const streets = this.getOptionsFromStrings(response.streets);
      this.streets = streets;
    }
  };

  private autoSetValueOnOneOption = (
    options: IDropdownOption [], fieldName: keyof IAddressAttributes,
  ): void => {
    // auto select in the case that field value is not set to void overriding address in edit model
    if (options?.length === 1
      && options[0].value !== this.formStore.values[fieldName]
      && !this.formStore.values[fieldName]) {
      this.formStore.setFieldValue(fieldName, options[0].value);
      if (fieldName === FIELD_TYPE.STATE) { this.onChangeState(options[0].value); }
      if (fieldName === FIELD_TYPE.CITY) { this.onChangeCity(options[0].value); }
    }
  };

  @action
  public setTowns(towns: string[]): void {
    this.towns = towns;
  }

  @action
  public setDistricts(districts: string[]): void {
    this.districts = districts;
  }

  public errorFieldsToHighlight = (formType: NOTIFICATION_GROUP): string[] => {
    switch (true) {
      case this.notificationsCollection.hasNotification(ADDRESS.INVALID_ZIP, formType):
        return [FIELD_TYPE.ZIPCODE];
      case this.notificationsCollection.hasNotification(ADDRESS.INVALID_STATE, formType):
        return [FIELD_TYPE.STATE];
      case this.notificationsCollection.hasNotification(ADDRESS.INVALID_CITY, formType):
        return [FIELD_TYPE.CITY];
      case this.notificationsCollection.hasNotification(ADDRESS.INVALID_ADDRESS, formType):
        return [FIELD_TYPE.ZIPCODE, FIELD_TYPE.STATE, FIELD_TYPE.CITY];
      case this.notificationsCollection.hasNotification(ADDRESS.INVALID_STATE_ZIP, formType):
      case this.notificationsCollection.hasNotification(ADDRESS.INVALID_ZIP_FOR_PROVINCE, formType):
        return [FIELD_TYPE.ZIPCODE, FIELD_TYPE.STATE];
      case this.notificationsCollection.hasNotification(ADDRESS.INVALID_AUPOST_QAS_ADDRESS, formType):
        return [FIELD_TYPE.ZIPCODE, FIELD_TYPE.STATE, FIELD_TYPE.CITY, FIELD_TYPE.FIRST_LINE_ADDRESS, FIELD_TYPE.SECOND_LINE_ADDRESS];
      case this.notificationsCollection.hasNotification(ADDRESS.INVALID_NAMES, formType):
        return [FIELD_TYPE.FIRST_NAME, FIELD_TYPE.LAST_NAME];
      default:
        return [];
    }
  };

  @action
  setShouldShowDeleteModal = (shouldShowDeleteModal: boolean): void => {
    this.shouldShowDeleteModal = shouldShowDeleteModal;
  };

  @computed
  get shouldRenderLegacyAddressDesign(): boolean {
    // TODO: update design for guest flow RCO-9408
    return (
      !this.configRepository?.config?.useNewAddressDesign
        || !this.transactionRepository.transaction.user.isUserRegistered
    );
  }

  @computed
  protected get shouldShowAddressDropdown(): boolean {
    return this.addressRepository?.addresses?.length > 1;
  }
}
